import _ from 'underscore';
import { mergeClasses, makeResetStyles } from '@fluentui/react-components';
import React, { useEffect, useState, useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  FolderAssetType,
  AdsStudioSpinner,
  FilterContext,
  ImagesFilterBar,
  useAdsStudioServices,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { Overlay } from '@bingads-webui-react/fabric-control';
import { usePageActiveTimeTracker } from '@bingads-webui-campaign-react/common-hooks';

import { ImagesTopBannerComponent, ImageGridView, ImageEmptyView } from './components';
import { useImagesPageStyle } from '../styles';

const useBaseSearchBoxStyles = makeResetStyles({
  '& .search-box': {
    minWidth: '450px',
  },
});

const ImagesPage = ({
  i18n,
  aid,
  cid,
  oDataConfig,
  dataService,
  notificationApi,
  logger,
  navigation,
  adsStudioUserPreferences,
  isAdsStudioAIGCMVPEnabled,
  isAIGCRegionRestricted,
  reload,
  pagePerfMarker: imagePagePerfMarker,
  setDataLoaded,
  permissions,
}) => {
  const classes = useImagesPageStyle();
  const searchBoxClasses = useBaseSearchBoxStyles();

  const {
    totalAssetCount,
    loadData,
  } = dataService;

  const cropDataService = useAdsStudioServices();

  const [isPageInited, setPageInited] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const { filterBar } = useContext(FilterContext);

  const {
    searchText = '',
    sortBy = null,
  } = useMemo(() => filterBar.getFilters(), [filterBar]);

  const onFailedToLoadData = useCallback(() => {
    notificationApi.showWarning(i18n.getString(_TL_('Failed to load images. Please refresh the page and try again.')));
  }, [i18n, notificationApi]);

  // init page
  useEffect(() => {
    if (!isPageInited) {
      setPageInited(true);
    }
  }, [isPageInited]);

  usePageActiveTimeTracker(logger, 'Image');

  // load data for initial page load
  useEffect(() => {
    if (!isPageInited) {
      // wait until page is initialized
      return;
    }

    if (reload) { // Important! Add a trivial log so that reload will be an explicit dep.
      logger.logTrace('force to reload');
    }
    imagePagePerfMarker.willFetchData();
    setLoadingData(true);
    loadData({
      assetTypeFilter: FolderAssetType.Image,
      isGlobalSearch: true,
      isSemanticSearch: true,
      searchText,
      sortBy,
      perfMarker: imagePagePerfMarker.createChild('imagePageLoadData'),
    })
      .catch(() => {
        onFailedToLoadData();
      }).finally(() => {
        setLoadingData(false);
        imagePagePerfMarker.willRender();
        if (setDataLoaded) {
          setDataLoaded();
        }
      });
  }, [isPageInited, loadData, onFailedToLoadData, searchText, sortBy, reload, logger]);

  const isPageLoading = !isPageInited || loadingData;

  return (
    <div className={mergeClasses('image-page-container', classes.imagePage)}>
      {isPageLoading && (
        <Overlay>
          <AdsStudioSpinner className="page-load-spinner" useCustomSize customStyle={{ margin: 200 }} />
        </Overlay>
      )}
      <ImagesTopBannerComponent
        isAdsStudioAIGCMVPEnabled={isAdsStudioAIGCMVPEnabled}
        isAIGCRegionRestricted={isAIGCRegionRestricted}
        notificationApi={notificationApi}
        i18n={i18n}
        aid={aid}
        cid={cid}
        logger={logger}
        oDataConfig={oDataConfig}
        navigation={navigation}
        permissions={permissions}
      />
      <ImagesFilterBar
        searchBoxClasses={searchBoxClasses}
        i18n={i18n}
        logger={logger}
        searchboxPlaceHolder={i18n.getString(_TL_("Enter an image name, ID, or a description of the image's content."))}
        userPreference={adsStudioUserPreferences}
      />
      {
        totalAssetCount && totalAssetCount > 0 ?
          <ImageGridView
            i18n={i18n}
            logger={logger}
            dataService={dataService}
            onFailedToLoadData={onFailedToLoadData}
            searchText={searchText}
            navigation={navigation}
            cropDataService={cropDataService}
          /> :
          <ImageEmptyView i18n={i18n} isSearchResult={!_.isEmpty(searchText)} />
      }
    </div>
  );
};

ImagesPage.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  dataService: PropTypes.shape({}),
  notificationApi: PropTypes.shape({}),
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  aid: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  adsStudioUserPreferences: PropTypes.shape({}),
  reload: PropTypes.shape({}),
  navigation: PropTypes.shape({}).isRequired,
  isAIGCRegionRestricted: PropTypes.bool,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({}),
  }),
};

ImagesPage.defaultProps = {
  dataService: {},
  notificationApi: {},
  adsStudioUserPreferences: undefined,
  reload: null,
  isAIGCRegionRestricted: false,
  permissions: { dynamic: {} },
};

export const ImagesPageComponent = ImagesPage;
